/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import { Provider as StoreProvider } from 'react-redux';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import { v4 as uuidv4 } from 'uuid';
import { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import App from './FeatureApp';
import { store } from './reducers';
import { GRAPHQL_GATEWAY_URL, IS_LOCAL_ENV } from './constants/environment';
import { MOCK_LOCAL_CONFIG } from './mocks';
import { getDesignLibrary, getThemeName } from '../alpha-helper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'dealer-context-service': DealerContextServiceV1;
}

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const superGraphLink = createHttpLink({
  uri: GRAPHQL_GATEWAY_URL,
});

const apolloClient = new ApolloClient({
  // CHANGE LATER
  // uri: IS_LOCAL_ENV ? 'http://localhost:4000/graphql' : process.env.GRAPHQL_GATEWAY,
  link: concat(correlationIdLink, superGraphLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeConfig = (config: any) => {
  if (config?.__type === 'aem-headless') {
    return config?.config?.fields;
  }

  return config;
};

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      '@volkswagen-onehub/onegraph-client': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  create: ({
    featureServices,
    config: remoteConfig,
  }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    const dealerContextService = featureServices['dealer-context-service'];
    loggerService?.info('Feature App created.');

    const configProvider = featureServices['gfa:service-config-provider'];

    const normalConfig = IS_LOCAL_ENV ? MOCK_LOCAL_CONFIG : normalizeConfig(remoteConfig);

    return {
      render: () => {
        return (
          <ThemeProvider theme={{ name: getThemeName() }}>
            <StoreProvider store={store}>
              <ApolloProvider client={apolloClient}>
                <App
                  configProvider={configProvider}
                  remoteConfig={normalConfig}
                  dealerContextService={dealerContextService}
                />
              </ApolloProvider>
            </StoreProvider>
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
